import { createAsyncThunk } from '@reduxjs/toolkit';
import config from '../constants/config';

/**
 * Replaces URL parameters with actual values
 * @param {string} urlPath - URL with parameters like '/api/users/:id'
 * @param {Array} params - Array of parameter objects like [{"id": 123}]
 * @returns {string} - URL with parameters replaced
 */
const replaceUrlParams = (urlPath, params = []) => {
  let processedUrl = urlPath;

  // Flatten all parameter objects into one
  const allParams = params.reduce((acc, paramObj) => {
    return { ...acc, ...paramObj };
  }, {});

  // Replace all :paramName with actual values
  Object.entries(allParams).forEach(([key, value]) => {
    const encoded = encodeURIComponent(value);
    processedUrl = processedUrl.replace(`:${key}`, encoded);
  });

  return processedUrl;
};

/**
 * Creates an API thunk with automatic token extraction
 * @param {string} actionName - Redux action name
 * @param {string} urlPath - API endpoint path (can include :params)
 * @param {string} method - HTTP method
 * @returns {AsyncThunk} - Redu * @param {object} [thunkOptions]
 * @param {function} [thunkOptions.beforeRequest] - (payload, getState, dispatch) => string|null — si string, l'API n'est pas appelée
 * @returns {AsyncThunk} - Redux async thunk
 */
const createApiThunk = (actionName, urlPath, method) => {
  return createAsyncThunk(
    actionName,
    async (payload = {}, { getState, rejectWithValue }) => {
      try {
        const state = getState();
        const token = state.auth.token;
        const url = config.SERVER_URL;
        const fullUrl = `${url.replace(/\/$/, '')}/${urlPath.replace(/^\//, '')}`;

        // Extract payload parts
        const { body, options = {}, params = [] } = payload;
        if (!Array.isArray(params)) {
          return rejectWithValue('params must be an array of objects');
        }
        // Replace URL parameters
        const finalUrl = replaceUrlParams(fullUrl, params);

        // Prepare headers
        const headers = {
          'Content-Type': 'application/json',
          ...options.headers,
        };

        // Add Authorization header if token exists
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        // Prepare request configuration
        const requestConfig = {
          method,
          headers,
          ...options, // Spread any additional fetch options
        };

        // Add body for requests that can have one
        if (body && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
          if (body instanceof FormData) {
            requestConfig.body = body; // Don't stringify FormData
            // Remove Content-Type header to let browser set it automatically
            delete headers["Content-Type"];
          } else {
            requestConfig.body = JSON.stringify(body);
          }
        }

        const urlObj = new URL(finalUrl);

        if (options.query && typeof options.query === "object") {
          Object.entries(options.query).forEach(([k, v]) => {
            if (v === undefined || v === null) return;
            urlObj.searchParams.append(k, String(v));
          });
        }
        const response = await fetch(urlObj.toString(), requestConfig);

        // Handle non-200 responses
        if (!response.ok) {
          const errorData = await response.text();
          let errorMessage;

          try {
            const parsedError = JSON.parse(errorData);
            errorMessage = parsedError.message || parsedError.error || `HTTP ${response.status}`;
          } catch {
            errorMessage = errorData || `HTTP ${response.status}`;
          }

          return rejectWithValue(errorMessage);
        }

        // Parse response
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          return await response.text();
        }

      } catch (error) {
        return rejectWithValue(error.message || 'Network error occurred');
      }
    }
  );
};

/**
 * API Factory with convenience methods
 */
export const factory = {
  /**
   * Create GET request thunk
   * @param {string} actionName - Redux action name
   * @param {string} urlPath - API endpoint path
   */
  get: (actionName, urlPath) => createApiThunk(actionName, urlPath, 'GET'),

  /**
   * Create POST request thunk
   * @param {string} actionName - Redux action name
   * @param {string} urlPath - API endpoint path
   * @param {object} [options] - voir createApiThunk (beforeRequest)
   */
  post: (actionName, urlPath) => createApiThunk(actionName, urlPath, 'POST'),

  /**
   * Create PUT request thunk
   * @param {string} actionName - Redux action name
   * @param {string} urlPath - API endpoint path
   */
  put: (actionName, urlPath) => createApiThunk(actionName, urlPath, 'PUT'),

  /**
   * Create DELETE request thunk
   * @param {string} actionName - Redux action name
   * @param {string} urlPath - API endpoint path
   */
  delete: (actionName, urlPath) => createApiThunk(actionName, urlPath, 'DELETE'),

  /**
   * Create PATCH request thunk
   * @param {string} actionName - Redux action name
   * @param {string} urlPath - API endpoint path
   */
  patch: (actionName, urlPath) => createApiThunk(actionName, urlPath, 'PATCH'),
}